// import { ICellEditorParams } from "ag-grid-community";
// import { Select } from "antd";
// import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

// type Option = {
//     label: string;
//     value: string;
// };

// export type BasicSelectEditor = ICellEditorParams & {
//     options: Option[];
// };
// export const BasicSelectEditor = forwardRef<
//     { getValue: () => string },
//     BasicSelectEditor
// >((params, ref) => {
//     const [value, setValue] = useState<string>(params.value);
//     const valueRef = useRef<string>(params.value);
//     const selectRef = useRef<any>(null);

//     useImperativeHandle(ref, () => ({
//         getValue() {
//             return valueRef.current;
//         },
//     }));

//     useEffect(() => {
//         queueMicrotask(() => selectRef.current?.focus());
//     }, [])

//     return (
//         <Select ref={selectRef.current} value={value} onChange={v => {
//             valueRef.current = v;
//             setValue(v);
//             params.stopEditing();
//         }}>
//             {params.options.map((o) => (
//                 <Select.Option key={o.value} value={o.label}>
//                     {o.label}
//                 </Select.Option>
//             ))}
//         </Select>
//     )
// });

import type { ICellEditorParams } from 'ag-grid-community'
import { Select, type SelectProps } from 'antd'
import type { DefaultOptionType } from 'antd/es/select'
import type { ScrollConfig } from 'rc-virtual-list/lib/List'
import {
  type Ref,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

type ScrollTo = (arg: number | ScrollConfig) => void

type BaseSelectRef = {
  focus: (options?: FocusOptions) => void
  blur: () => void
  scrollTo: ScrollTo
  nativeElement: HTMLElement
}

type KeyType = string | number | null | undefined
type ModeType = SelectProps['mode']

type SelectOptionsType<K extends KeyType> = Omit<DefaultOptionType, 'value'> & {
  value?: K
  children?: Omit<SelectOptionsType<K>, 'children'>[]
}
type SelectValueType<
  K extends KeyType,
  M extends ModeType = undefined,
> = M extends undefined ? K : Array<K> | undefined

/**
 * todo: this actually gives us way better type definitions than the default ones
 * (all value-related types are exactly what option.value is) - either open a PR in
 * the antD repo, or find a way to modify the antD type to return this instead.
 */
type BetterSelectProps<K extends KeyType, M extends ModeType = undefined> = {
  options: SelectOptionsType<K>[]
  mode?: M
} & Omit<
  SelectProps<SelectValueType<K, M>, SelectOptionsType<K>>,
  'mode' | 'options'
>

type BasicSelectEditorProps<
  K extends KeyType,
  M extends ModeType = undefined,
  RowType = unknown,
  TContext = unknown,
> = Omit<BetterSelectProps<K, M>, 'onKeyDown'> &
  ICellEditorParams<RowType, SelectValueType<K, M>, TContext>

function _BasicSelectEditor<
  K extends KeyType,
  M extends ModeType = undefined,
  RowType = unknown,
  TContext = unknown,
>(
  {
    columnApi,
    eventKey,
    colDef,
    rowIndex,
    cellStartedEdit,
    stopEditing,
    eGridCell,
    parseValue,
    formatValue,
    ...props
  }: BasicSelectEditorProps<K, M, RowType, TContext>,
  ref?: Ref<BaseSelectRef | null> | undefined,
) {
  const [value, setValue] = useState(props.value)
  const innerRef = useRef<BaseSelectRef>(null)
  const [visible, setVisible] = useState(true)
  useImperativeHandle(ref, () =>
    !innerRef.current
      ? null
      : {
          getValue() {
            return value
          },
          isCancelBeforeStart() {
            return false
          },
          isCancelAfterEnd() {
            return false
          },
          ...innerRef.current,
        },
  )

  useEffect(() => innerRef.current?.focus(), [])

  useEffect(() => {
    if (!visible) {
      stopEditing()
    }
  }, [visible, stopEditing])

  const onChange = useCallback<Required<BetterSelectProps<K, M>>['onChange']>(
    (v, o) => {
      if (value === v) return
      setValue(v)
      props.onChange?.(v, o)
    },
    [value, props.onChange],
  )

  const onKeyDown = useCallback(
    (_event: React.KeyboardEvent<HTMLDivElement>) => {
      /** */
    },
    [props.onKeyDown],
  )

  return (
    <Select
      {...props}
      style={{ width: '100%', boxSizing: 'border-box', ...props.style }}
      onKeyDown={onKeyDown}
      onChange={onChange}
      onDropdownVisibleChange={setVisible}
      ref={innerRef}
      value={value}
      autoFocus
      defaultOpen
    />
  )
}

export const BasicSelectEditor = forwardRef(_BasicSelectEditor) as <
  K extends KeyType,
  M extends ModeType = undefined,
  RowType = unknown,
  TContext = unknown,
>(
  props: BasicSelectEditorProps<K, M, RowType, TContext> & {
    ref?: Ref<unknown> | undefined
  },
) => ReturnType<typeof _BasicSelectEditor<K, M, RowType, TContext>>
